import React from 'react';
import { ScrollView, Text, TouchableOpacity, View, useWindowDimensions } from 'react-native';
import { ThemeColors } from '@/src/context/ThemeContext';
import { BlogPost } from '@/src/domain/models/BlogPost';
import { useTranslation } from '@/src/i18n';
import { createStyles } from './BlogListScreen.styles';

export interface BlogListScreenProps {
  posts: BlogPost[];
  colors: ThemeColors;
  onPostPress: (slug: string) => void;
  onBack: () => void;
}

export const BlogListScreen: React.FC<BlogListScreenProps> = ({
  posts,
  colors,
  onPostPress,
  onBack,
}) => {
  const { width } = useWindowDimensions();
  const isWide = width >= 768;
  const styles = createStyles(isWide);
  const { t, language } = useTranslation();

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString(language === 'es' ? 'es-ES' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <ScrollView
      style={[styles.container, { backgroundColor: colors.background }]}
      contentContainerStyle={styles.scrollContent}
    >
      <TouchableOpacity style={styles.backBtn} onPress={onBack}>
        <Text style={[styles.backText, { color: colors.primary }]}>← Tournaly</Text>
      </TouchableOpacity>

      <View style={styles.pageHeader}>
        <Text style={[styles.pageTitle, { color: colors.text }]}>{t('blog.title')}</Text>
        <Text style={[styles.pageSubtitle, { color: colors.textSecondary }]}>
          {t('blog.subtitle')}
        </Text>
      </View>

      {posts.map((post) => {
        const displayTitle = language === 'es' ? post.titleEs : post.title;
        const displaySummary = language === 'es' ? post.summaryEs : post.summary;

        return (
          <TouchableOpacity
            key={post.slug}
            style={[styles.card, { backgroundColor: colors.card, borderColor: colors.border }]}
            onPress={() => onPostPress(post.slug)}
            activeOpacity={0.8}
          >
            <View style={styles.tagsRow}>
              {post.tags.map((tag) => (
                <View key={tag} style={[styles.tagPill, { borderColor: colors.primary }]}>
                  <Text style={[styles.tagText, { color: colors.primary }]}>{tag}</Text>
                </View>
              ))}
            </View>

            <Text style={[styles.cardTitle, { color: colors.text }]}>{displayTitle}</Text>

            <Text style={[styles.cardDate, { color: colors.textTertiary }]}>
              {formatDate(post.date)}
            </Text>

            <Text style={[styles.cardSummary, { color: colors.textSecondary }]} numberOfLines={3}>
              {displaySummary}
            </Text>

            <TouchableOpacity style={styles.readMoreBtn} onPress={() => onPostPress(post.slug)}>
              <Text style={[styles.readMoreText, { color: colors.primary }]}>
                {t('blog.readMore')}
              </Text>
            </TouchableOpacity>
          </TouchableOpacity>
        );
      })}
    </ScrollView>
  );
};
